import { CommonModule } from '@angular/common';
import { Component, HostListener, OnInit } from '@angular/core';
import {
  NavigationEnd,
  Router,
  RouterModule,
  Event as NavigationEvent,
} from '@angular/router';
import { Observable, Subscription } from 'rxjs';
import { filter, startWith, switchMap } from 'rxjs/operators';
import { SidebarService } from '../../../core/services/sidebar/sidebar.service';
import {
  IDeployVersion,
  ISidebarMenu,
} from '../../../core/models/interface/sidebar/sidebar-menu';
import { AuthService } from '../../../core/services/auth-service/auth.service';
import { IUserProfileDetails } from '../../../core/models/interface/login/i-login';
import { APP_VERSION } from '../../../app-version';
import { ThaiDatePipe } from '../../pipes/thai-date.pipe';
import { NgbTooltipModule } from '@ng-bootstrap/ng-bootstrap';
import { NotificationService } from '../../../core/services/api/notification/notification.service';
import { NgxSpinnerService } from 'ngx-spinner';
import { ApiResponse } from '../../../core/models/interface/api-response/api-response';
import { StatusCodeEnum } from '../../../core/models/enum/status-code-enum';
import { NotificationShared } from '../../notification/notification.service';

@Component({
  selector: 'app-sidebar',
  standalone: true,
  imports: [RouterModule, CommonModule, NgbTooltipModule],
  templateUrl: './sidebar.component.html',
  styleUrl: './sidebar.component.scss',
})
export class SidebarComponent implements OnInit {
  router$: Subscription = new Subscription();
  currentUser$: Subscription = new Subscription();

  toggleSidebar: boolean = false;
  isMediumScreen: boolean = false;

  menus: ISidebarMenu[] = [];
  userLoginInfo!: IUserProfileDetails;

  appVersion = APP_VERSION;
  apiVersionText: string = '';

  apiVersionResponse!: IDeployVersion;

  notificationAll!: any

  constructor(
    private router: Router,
    private sidebarSrv: SidebarService,
    private authSrv: AuthService,
    private spinner: NgxSpinnerService,
    private notificationSrv: NotificationService,
    private notificationShared: NotificationShared,
  ) {
    this.router$ = this.router.events
      .pipe(filter((event: NavigationEvent) => event instanceof NavigationEnd))
      .subscribe((navigation: NavigationEvent) => {
        if (navigation instanceof NavigationEnd) {
          this.checkMenuExpanded(navigation.urlAfterRedirects);
        }
      });
  }

  @HostListener('window:resize')
  onWindowResize() {
    this.isMediumScreen = window.innerWidth < 768;
  }

  ngOnInit() {
    this.getApiVersionDeploy();
    this.onWindowResize();
    this.getUserDetails();

    // โหลด noti ครั้งแรก
    this.notificationShared.refreshNotifications();

    // this.router.events.pipe(
    //   filter(event => event instanceof NavigationEnd),
    //   startWith(null), // เริ่มโหลดตั้งแต่ตอนแรก
    //   switchMap(() => this.notificationShared.notifications$)
    // ).subscribe(data => {
    //   // console.log('📩 Sidebar received noti (even after route change)', data);
    //   this.notificationAll = data;
    //   this.menus = this.mapNotificationsToMenus(this.menus, this.notificationAll);
    // });

    this.router.events
    .pipe(filter(event => event instanceof NavigationEnd))
    .subscribe(() => {
      // console.log('🔄 Route changed — refresh notifications');
      this.notificationShared.refreshNotifications();
    });

    this.notificationShared.notifications$.subscribe(data => {
      // console.log('📩 Sidebar received noti', data);
      this.notificationAll = data;
      this.menus = this.mapNotificationsToMenus(this.menus, this.notificationAll);
    });


  }

  ngOnDestroy() {
    if (this.router$) this.router$.unsubscribe();
    if (this.currentUser$) this.currentUser$.unsubscribe();
  }

  getUserDetails() {
    this.currentUser$ = this.authSrv.currentUserLogin$.subscribe(
      (res: IUserProfileDetails | null) => {
        if (res && res.user_id) {
          this.userLoginInfo = res;

          if (res.visibleSubMenuIds || res.visibleChildrenSubMenuIds) {
            const viewAbleSubMenuIds = res.visibleSubMenuIds;
            const viewAbleChildrenSubMenuIds = res.visibleChildrenSubMenuIds;
            this.menus = this.sidebarSrv.getMenuByPermissions(
              viewAbleSubMenuIds,
              viewAbleChildrenSubMenuIds
            );

            // If a specific login type was used, limit the sidebar to that submenu only
            const savedType = this.authSrv.getSavedSubMenuCode();
            if (savedType) {
              const targetSubIds = this.authSrv.getSubMenuIdsForLoginType(savedType);
              if (targetSubIds && targetSubIds.length) {
                this.menus = this.menus
                  .map((menu) => {
                    // filter mainMenus of this menu to only include those matching targetSubIds
                    if (menu.mainMenus && menu.mainMenus.length) {
                      const filteredMainMenus = menu.mainMenus.filter((mm: any) =>
                        mm.subMenuId ? targetSubIds.includes(mm.subMenuId) : false
                      );
                      return { ...menu, mainMenus: filteredMainMenus };
                    }
                    // top-level menu with subMenuId
                    if (menu.subMenuId && targetSubIds.includes(menu.subMenuId)) {
                      return menu;
                    }
                    return { ...menu, mainMenus: [] };
                  })
                  .filter((m) => (m.mainMenus && m.mainMenus.length) || m.subMenuId && targetSubIds.includes(m.subMenuId));
              }
            }

            this.checkMenuExpanded(this.router.url);
          }
        }
      }
    );
  }

  mapNotificationsToMenus(menus: any[], noti: any[]): any[] {
    return menus.map(menu => {

      const found = noti.find(n => n.menu_code === menu.menu_code);
      menu.notification_count = found ? found.notification_count : 0;

      // ถ้ามี mainMenus
      if (menu.mainMenus && menu.mainMenus.length) {
        menu.mainMenus = this.mapNotificationsToMenus(menu.mainMenus, noti);
      }

      // ถ้ามี subMenus
      if (menu.subMenus && menu.subMenus.length) {
        menu.subMenus = this.mapNotificationsToMenus(menu.subMenus, noti);
      }

      // ถ้ามี childSubMenus
      if (menu.childSubMenus && menu.childSubMenus.length) {
        menu.childSubMenus = this.mapNotificationsToMenus(menu.childSubMenus, noti);
      }

      return menu;
    });
  }


  checkMenuExpanded(currentRoute: string) {
    this.menus.forEach((menu) => {
      menu.expanded = menu.mainMenus.some((mainMenu) => {
        if (!mainMenu.link) {
          return this.isCurrentUrlInMenuArray(mainMenu, currentRoute);
        } else {
          if (currentRoute.startsWith(mainMenu.link)) {
            this.setAllSubMenuToDefault();
          }
          return currentRoute.startsWith(mainMenu.link);
        }
      });
    });
  }

  setAllSubMenuToDefault() {
    const expandedWithSubMenus = this.menus
      .flatMap((item) => item.mainMenus || [])
      .filter((menu) => menu.expanded && menu.subMenus);
    expandedWithSubMenus.forEach((menu) => (menu.expanded = false));
  }

  isCurrentUrlInMenuArray(menu: any, currentPath: string) {
    if (menu.subMenus) {
      return (menu.expanded = menu.subMenus.some((data: any) => {
        return currentPath.startsWith(data.link);
      }));
    }
  }

  toggleMenu(menu: any) {
    this.menus.forEach((cm) => {
      if (cm.title === menu.title) {
        if (this.isMediumScreen) {
          this.toggleSidebar = true;
          const currentUrl = this.router.url;

          let isSubMenu = menu.mainMenus.find(
            (d: any) => !!d.subMenus && d.subMenus.length > 0
          );
          if (!isSubMenu) {
            const existActivePath = cm.mainMenus?.some(
              (m) => m.link && currentUrl.startsWith(m.link)
            );

            cm.expanded = !cm.expanded || (!cm.expanded && existActivePath);
          } else {
            cm.mainMenus.forEach((x) => {
              const existActiveSubPath = x.subMenus?.some(
                (subMainP) =>
                  subMainP.link && currentUrl.startsWith(subMainP.link)
              );

              if (!existActiveSubPath) {
                cm.expanded = !cm.expanded;
              } else {
                isSubMenu.expanded = !isSubMenu.expanded && existActiveSubPath;
                cm.expanded = isSubMenu.expanded;
              }
            });
          }
        } else {
          cm.expanded = !cm.expanded;
        }
      } else {
        cm.expanded = false;

        if (cm.mainMenus?.some((m) => m.title === menu.title)) {
          cm.expanded = true;
          cm.mainMenus.forEach((m) => {
            m.expanded = m.title === menu.title ? !m.expanded : false;
          });
        } else {
          cm.mainMenus?.forEach((m) => (m.expanded = false));
        }
      }
    });
  }

  onToggleSidebar() {
    this.toggleSidebar = !this.toggleSidebar;

    if (this.isMediumScreen) {
      if (this.toggleSidebar) {
        const currentUrl = this.router.url;

        this.menus.forEach((menu) => {
          if (!menu.mainMenus) return;

          const isSubMenu = menu.mainMenus.find(
            (mainMenu) => mainMenu.subMenus?.length
          );

          if (isSubMenu) {
            const existSubMenu =
              isSubMenu.subMenus?.find(
                (sm) => sm.link && currentUrl.startsWith(sm.link)
              ) || null;

            if (existSubMenu) {
              menu.expanded = isSubMenu.expanded = true;
              return;
            }
          }

          menu.expanded = menu.mainMenus.some(
            (mmu) => mmu.link && currentUrl.startsWith(mmu.link)
          );
        });
      } else {
        this.menus.forEach((menu) => {
          menu.expanded = false;
        });
      }
    }
  }

  updateSingleMenu() {
    this.toggleSidebar = true;
    this.menus.forEach((m) => {
      m.expanded = false;
    });
  }

  getApiVersionDeploy() {
    this.sidebarSrv
      .getApiCurrentVersion()
      .subscribe((response: IDeployVersion) => {
        if (!!response) {
          this.apiVersionResponse = response;
        }
      });
  }
}
